<template>
  <AppDialog
    v-model:open="open"
    :dismissible="saved"
    :auto-focus="false"
    :title-hidden="saved"
    :title="saved ? feedback.feedbackTitle : t('nkf.homeCards.feeling')"
    :description="saved ? '' : t('nkf.homeCards.moodDescription')"
    description-class="tw:-mt-0.5"
  >
    <template #title
      ><span class="mood-title tw:type-dialog tw:tracking-normal">{{
        saved ? feedback.feedbackTitle : t('nkf.homeCards.feeling')
      }}</span></template
    >
    <template v-if="saved" #illustration
      ><AppDialogIllustration src="/images/nkf/nurse.webp" background="var(--card)">
        <div class="mood-bubble tw:absolute tw:pt-3.5 tw:pr-3.5 tw:pb-0 tw:pl-4.5">
          <b class="tw:type-section tw:block tw:tracking-normal">{{ feedback.feedbackTitle }}</b>
          <p class="tw:type-secondary tw:text-(--muted-foreground) tw:mt-2">
            {{ t('nkf.moodDetails.saved', { mood: savedMoodName }) }}
          </p>
        </div>
      </AppDialogIllustration></template
    >
    <blockquote
      v-if="saved"
      class="mood-quote tw:text-start tw:text-primary tw:p-4 tw:rounded-(--radius-card) tw:bg-(--surface-input)"
    >
      <AppIcon size="action" name="heart" />
      <p class="tw:type-editorial-quote">“{{ feedback.feedbackContent }}”</p>
      <small class="tw:type-unit tw:text-(--muted-foreground) tw:block tw:mt-1.5"
        >— {{ t('nkf.moodDetails.nurse') }}</small
      >
    </blockquote>
    <template v-if="!saved">
      <MoodChoices
        v-model="emotionMainUnique"
        :readonly="isSubmitting"
        @update:model-value="clearErrors('emotionMainUnique')"
        :options="options"
        stacked
        :face-size="40"
        class="mood-choices tw:-mt-0.5"
      />
      <FieldError v-if="errors.emotionMainUnique">{{ errors.emotionMainUnique }}</FieldError>
      <label for="mood-note" class="tw:sr-only">{{ t('nkf.notes') }}</label>
      <Textarea
        id="mood-note"
        :disabled="isSubmitting"
        v-model="note"
        @update:model-value="clearErrors('notes')"
        :maxlength="FORM_LIMIT.NOTE"
        :placeholder="t('nkf.homeCards.moodNote')"
        rows="2"
        class="mood-note tw:min-h-17.5 tw:resize-none tw:-mt-1"
      />
      <FieldError v-if="errors.notes">{{ errors.notes }}</FieldError>
    </template>
    <template #footer>
      <Button v-if="saved" @click="open = false">{{ t('nkf.moodDetails.thankYou') }}</Button>
      <Button v-else :disabled="!emotionMainUnique || isSubmitting" @click="save">{{
        t('nkf.followup.submit')
      }}</Button>
    </template>
  </AppDialog>
</template>
<script setup>
// 1. Import necessary components
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { FORM_LIMIT } from '@/shared/constants/forms';
import homeService from '@/shared/services/home.service';
import { useValidate } from '@/shared/composables/useValidate';
import { useToastService } from '@/shared/composables/useToastService';
import { emotionCheckinSchema } from '@/validations/forms/emotion-checkin';
import AppDialog from '@/shared/components/AppDialog.vue';
import AppDialogIllustration from '@/shared/components/AppDialogIllustration.vue';
import AppIcon from '@/shared/components/AppIcon.vue';
import MoodChoices from './MoodChoices.vue';
import { useNotificationsStore } from '@/core/stores/notifications.store';
import { MOOD_OPTIONS } from '../constants/symptom';
import { Button } from '@/shared/components/ui/button';
import { Textarea } from '@/shared/components/ui/textarea';
import { FieldError } from '@/shared/components/ui/field';
// 2. Use
const open = defineModel('open', { type: Boolean, default: false });
const emit = defineEmits(['submitted']);
const inbox = useNotificationsStore();
const props = defineProps({
  // Emotion check-in choices from the API: { mainUnique, name, photo, numericValue }.
  choices: { type: Array, required: true },
  questionDate: { type: String, required: true },
});
const { t } = useI18n();
const { errors, clearErrors, validateData } = useValidate();
const toast = useToastService();
// 3. Define a reactive variable
const emotionMainUnique = ref('');
const note = ref('');
const saved = ref(false);
const feedback = ref({ feedbackTitle: '', feedbackContent: '' });
const savedMoodName = ref('');
const isSubmitting = ref(false);
let responseRequest = null;
// 4. Define methods or computed properties if needed
const options = computed(() =>
  props.choices.map((choice) => ({
    value: choice.mainUnique,
    level: choice.numericValue,
    label: choice.name,
    photo: choice.photo,
    background: MOOD_OPTIONS.find((option) => option.value === choice.numericValue)?.background,
  })),
);
async function save() {
  if (isSubmitting.value) {
    return;
  }
  const details = {
    emotionMainUnique: emotionMainUnique.value,
    notes: note.value.trim(),
    questionDate: props.questionDate,
  };
  const key = JSON.stringify(details);
  if (responseRequest?.key !== key) {
    responseRequest = { key, requestId: crypto.randomUUID() };
  }
  const { isValid, data } = validateData(emotionCheckinSchema, {
    ...details,
    requestId: responseRequest.requestId,
  });
  if (!isValid) {
    return;
  }

  isSubmitting.value = true;
  try {
    feedback.value = await homeService.submitEmotionCheckin(data);
    if (feedback.value.alreadyAnswered) {
      toast.info(t('nkf.notificationDetails.alreadyAnswered'));
      open.value = false;
    } else {
      savedMoodName.value = feedback.value.emotionName;
      saved.value = true;
    }
    emit('submitted');
    await inbox.refreshCount();
  } finally {
    isSubmitting.value = false;
  }
}
// 5. VueJS Hooks
</script>
<style scoped>
/* The dialog grid adds a 1rem gap; the design keeps 14px above the options, 12px above the note and 16px above the actions. */
.mood-bubble {
  left: 44%;
  top: 6%;
  width: 53%;
}
.mood-quote svg {
  margin-bottom: 6px;
  color: var(--rose);
}
</style>
